import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { FilterState } from '../../../components/PageFilters/types';
import {
  AuditTrailFilterDateTimeValue,
  AuditTrailFilterMetadata,
  AuditTrailFilterStringRegEx,
} from '../components/AuditTrailPageControls/types';
import { filterRegistry, getAllFilterParams } from '../config/filterRegistry';
import {
  clearAllFilters,
  getBooleanFilter,
  getDateTimeFilter,
  getMetadataFilter,
  getRegexFilter,
  getStringFilter,
  setBooleanFilter,
  setDateTimeFilter,
  setMetadataFilter,
  setRegexFilter,
  setStringFilter,
} from '../utils/urlSync';

const EMPTY_FILTERS = {};

/**
 * Hook for managing Audit Trail filter state with URL synchronization.
 * Automatically syncs filter changes to URL parameters and provides filter state management.
 */
export const useAuditTrailFilterSync = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Process URL parameters into filter state based on registry configuration
  const filters = useMemo<Record<string, FilterState<any>>>(() => {
    const filterObj: Record<string, FilterState<any>> = {};

    // Process each filter based on its type
    Object.values(filterRegistry).forEach((filterDef) => {
      let filterValue: any = null;

      switch (filterDef.type) {
        case 'dateTime':
          filterValue = getDateTimeFilter(searchParams, 'dateStart', 'dateEnd');
          break;
        case 'string':
          filterValue = getStringFilter(searchParams, filterDef.urlParams[0]);
          break;
        case 'regex':
          filterValue = getRegexFilter(searchParams, filterDef.urlParams[0], filterDef.urlParams[1]);
          break;
        case 'boolean':
          filterValue = getBooleanFilter(searchParams, filterDef.urlParams[0]);
          break;
        case 'metadata':
          filterValue = getMetadataFilter(searchParams, filterDef.urlParams[0]);
          break;
      }

      if (filterValue) {
        const readableValueKey = `${filterDef.key}_readable`;
        const readableValue = searchParams.get(readableValueKey);

        filterObj[filterDef.key] = {
          value: filterValue,
          readableValue: readableValue || undefined,
        };
      }
    });

    return Object.keys(filterObj).length > 0 ? filterObj : EMPTY_FILTERS;
  }, [searchParams]);

  const showReadOnlyEvents = useMemo(() => {
    return getBooleanFilter(searchParams, 'readOnly');
  }, [searchParams]);

  const allWorkspaces = useMemo(() => {
    return getBooleanFilter(searchParams, 'allWorkspaces');
  }, [searchParams]);

  const setFilters = useCallback(
    (newFilters: Record<string, FilterState<any>>) => {
      const currentSearchParams = new URLSearchParams(location.search);

      getAllFilterParams().forEach((param) => currentSearchParams.delete(param));
      Object.values(filterRegistry).forEach((filterDef) => {
        currentSearchParams.delete(`${filterDef.key}_readable`);
      });

      // Set new filter values based on their type
      Object.values(filterRegistry).forEach((filterDef) => {
        const filterState = newFilters[filterDef.key];
        const filterValue = filterState?.value;

        if (!filterValue) return;

        switch (filterDef.type) {
          case 'dateTime':
            setDateTimeFilter(
              currentSearchParams,
              'dateStart',
              'dateEnd',
              filterValue as AuditTrailFilterDateTimeValue
            );
            break;
          case 'string':
            setStringFilter(currentSearchParams, filterDef.urlParams[0], filterValue as string);
            break;
          case 'regex':
            setRegexFilter(
              currentSearchParams,
              filterDef.urlParams[0],
              filterDef.urlParams[1],
              filterValue as AuditTrailFilterStringRegEx
            );
            break;
          case 'boolean':
            setBooleanFilter(currentSearchParams, filterDef.urlParams[0], filterValue as boolean);
            break;
          case 'metadata':
            setMetadataFilter(currentSearchParams, filterDef.urlParams[0], filterValue as AuditTrailFilterMetadata);
            break;
        }

        if (filterState?.readableValue) {
          const readableValueKey = `${filterDef.key}_readable`;
          currentSearchParams.set(readableValueKey, filterState.readableValue);
        }

        if (filterState?.readableValue) {
          const readableValueKey = `${filterDef.key}_readable`;
          currentSearchParams.set(readableValueKey, filterState.readableValue);
        }
      });

      const newSearch = currentSearchParams.toString();
      if (newSearch !== window.location.search.slice(1)) history.replace({ ...location, search: newSearch });
    },
    [history, location]
  );

  const setShowReadOnlyEvents = useCallback(
    (showReadOnly: boolean) => {
      const currentSearchParams = new URLSearchParams(location.search);
      setBooleanFilter(currentSearchParams, 'readOnly', showReadOnly);

      const newSearch = currentSearchParams.toString();
      if (newSearch !== window.location.search.slice(1)) history.replace({ ...location, search: newSearch });
    },
    [history, location]
  );

  const setAllWorkspaces = useCallback(
    (allWorkspaces: boolean) => {
      const currentSearchParams = new URLSearchParams(location.search);
      setBooleanFilter(currentSearchParams, 'allWorkspaces', allWorkspaces);

      const newSearch = currentSearchParams.toString();
      if (newSearch !== window.location.search.slice(1)) history.replace({ ...location, search: newSearch });
    },
    [history, location]
  );

  const clearFilters = useCallback(() => {
    const currentSearchParams = new URLSearchParams(location.search);

    const paramsToClear = [...getAllFilterParams()];
    Object.values(filterRegistry).forEach((filterDef) => {
      paramsToClear.push(`${filterDef.key}_readable`);
    });

    clearAllFilters(currentSearchParams, paramsToClear);

    const newSearch = currentSearchParams.toString();
    if (newSearch !== window.location.search.slice(1)) history.replace({ ...location, search: newSearch });
  }, [history, location]);

  return {
    filters,
    setFilters,
    clearFilters,
    showReadOnlyEvents,
    setShowReadOnlyEvents,
    allWorkspaces,
    setAllWorkspaces,
  };
};
